import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchGuides, guideHasPdf, guideVideos, searchGuides } from '../utils/helpVideos';
import '../styles/HelpVideos.css';

// Page publique /help/videos : recherche par mots-clés dans les guides
// (vidéos YouTube + PDF). Chaque résultat ouvre /guides/:id.
// ?q=eau pré-remplit la recherche, ?lng=fr force la langue.

const LANGUAGES = ['fr', 'it', 'en', 'de', 'es', 'pt', 'ru'];

const TEXTS = {
  fr: {
    title: 'Comment faire ?',
    placeholder: "Ex. : pas d'eau, coupure de courant, piscine…",
    loading: 'Chargement…',
    error: 'Une erreur provient du serveur, veuillez rafraîchir la page',
    noResult: 'Aucun résultat. Essayez un autre mot, ou écrivez-nous : nous vous enverrons la bonne vidéo.',
    results: 'Résultats',
    clear: 'Effacer',
    videos: (n) => (n > 1 ? `${n} vidéos` : '1 vidéo'),
    pdf: 'Guide PDF'
  },
  it: {
    title: 'Come fare?',
    placeholder: 'Es.: niente acqua, corrente saltata, piscina…',
    loading: 'Caricamento…',
    error: 'Errore dal server, aggiorna la pagina',
    noResult: 'Nessun risultato. Prova con un’altra parola, oppure scrivici: ti invieremo il video giusto.',
    results: 'Risultati',
    clear: 'Cancella',
    videos: (n) => (n > 1 ? `${n} video` : '1 video'),
    pdf: 'Guida PDF'
  },
  en: {
    title: 'How do I…?',
    placeholder: 'E.g. no water, power cut, pool…',
    loading: 'Loading…',
    error: 'Server error, please refresh',
    noResult: 'No results. Try another word, or message us: we will send you the right video.',
    results: 'Results',
    clear: 'Clear',
    videos: (n) => (n > 1 ? `${n} videos` : '1 video'),
    pdf: 'PDF guide'
  },
  de: {
    title: 'Wie geht das?',
    placeholder: 'Z. B. kein Wasser, Stromausfall, Pool…',
    loading: 'Laden…',
    error: 'Serverfehler, bitte aktualisieren Sie',
    noResult: 'Keine Ergebnisse. Versuchen Sie ein anderes Wort oder schreiben Sie uns: Wir schicken Ihnen das passende Video.',
    results: 'Ergebnisse',
    clear: 'Löschen',
    videos: (n) => (n > 1 ? `${n} Videos` : '1 Video'),
    pdf: 'PDF-Anleitung'
  },
  es: {
    title: '¿Cómo se hace?',
    placeholder: 'Ej.: no hay agua, corte de luz, piscina…',
    loading: 'Cargando…',
    error: 'Error del servidor, actualice',
    noResult: 'Sin resultados. Pruebe otra palabra o escríbanos: le enviaremos el vídeo adecuado.',
    results: 'Resultados',
    clear: 'Borrar',
    videos: (n) => (n > 1 ? `${n} vídeos` : '1 vídeo'),
    pdf: 'Guía PDF'
  },
  pt: {
    title: 'Como fazer?',
    placeholder: 'Ex.: sem água, falta de luz, piscina…',
    loading: 'Carregando…',
    error: 'Erro do servidor, atualize',
    noResult: 'Nenhum resultado. Tente outra palavra ou escreva-nos: enviaremos o vídeo certo.',
    results: 'Resultados',
    clear: 'Limpar',
    videos: (n) => (n > 1 ? `${n} vídeos` : '1 vídeo'),
    pdf: 'Guia PDF'
  },
  ru: {
    title: 'Как это сделать?',
    placeholder: 'Напр.: нет воды, нет света, бассейн…',
    loading: 'Загрузка…',
    error: 'Ошибка сервера, обновите',
    noResult: 'Ничего не найдено. Попробуйте другое слово или напишите нам — мы пришлём нужное видео.',
    results: 'Результаты',
    clear: 'Очистить',
    videos: (n) => `Видео: ${n}`,
    pdf: 'PDF-инструкция'
  }
};

const HelpVideosPage = () => {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const query = searchParams.get('q') || '';

  const languageCode = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const texts = TEXTS[languageCode] || TEXTS.en;

  useEffect(() => {
    const origin = window.location.origin;
    let cancelled = false;
    fetchGuides(origin)
      .then(({ guides: loaded }) => {
        if (!cancelled) setGuides(loaded);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Tuiles : les guides avec vidéos d'abord, puis ceux qui n'ont qu'un PDF.
  const tiles = useMemo(() => {
    const visible = guides.filter((guide) => guideVideos(guide).length > 0 || guideHasPdf(guide));
    return [
      ...visible.filter((guide) => guideVideos(guide).length > 0),
      ...visible.filter((guide) => guideVideos(guide).length === 0)
    ];
  }, [guides]);

  const results = useMemo(() => searchGuides(tiles, query), [tiles, query]);
  const isSearching = query.trim().length > 0;
  const shownGuides = isSearching ? results : tiles;

  const setQuery = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="help-page">
      <div className="help-page-inner">
        <header className="help-header">
          <a href="/">
            <img src="/images/logo.png" alt="Villa Pina e Paolo" />
          </a>
          <h1>{texts.title}</h1>
        </header>

        <div className="help-langs">
          {LANGUAGES.map((lang) => (
            <button
              key={lang}
              type="button"
              className={lang === languageCode ? 'active' : ''}
              onClick={() => i18n.changeLanguage(lang)}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="help-search">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={texts.placeholder}
            aria-label={texts.placeholder}
            enterKeyHint="search"
          />
          {isSearching && (
            <button type="button" className="help-search-clear" onClick={() => setQuery('')} aria-label={texts.clear}>
              ✕
            </button>
          )}
        </div>

        {loading && <p className="help-empty">{texts.loading}</p>}
        {error && <p className="help-empty">{texts.error}</p>}

        {!loading && !error && (
          <>
            {isSearching && results.length > 0 && <h2 className="help-results-title">{texts.results}</h2>}
            {isSearching && results.length === 0 ? (
              <p className="help-empty">{texts.noResult}</p>
            ) : (
              <ul className="help-tiles">
                {shownGuides.map((guide) => {
                  const videoCount = guideVideos(guide).length;
                  return (
                    <li key={guide.id}>
                      <Link to={`/guides/${guide.id}`} className="help-tile">
                        <span className="help-tile-icon" aria-hidden="true">{guide.icon || (videoCount ? '🎬' : '📄')}</span>
                        <span>{guide.label}</span>
                        <span className="help-tile-meta">{videoCount ? texts.videos(videoCount) : texts.pdf}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HelpVideosPage;
